import {
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { cx } from '@/utils/cx';

import { EntityBadge, type EntityBadgeProps } from './EntityBadge';
import {
  EntityHoverPopover,
  type EntityHoverPopoverPlacement,
} from './EntityHoverPopover';
import { WRAPPER_ALIGN_CLASSES } from './types';

const SHOW_DELAY = 300;
const HIDE_DELAY = 150;

export interface EntityBadgeWithHoverPreviewProps extends EntityBadgeProps {
  /** Body content to render inside the popover. */
  children: ReactNode;
  /** Popover placement. Defaults to 'top'. */
  placement?: EntityHoverPopoverPlacement;
}

export function EntityBadgeWithHoverPreview({
  children,
  placement = 'top',
  state,
  variant,
  avatar,
  disabled,
  interactive,
  onClick,
  className,
  showEmail,
}: EntityBadgeWithHoverPreviewProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  // Compute hover support synchronously via lazy initializer so the
  // initial render already reflects the touch-disabled path. Without this,
  // the first render wraps the badge in the popover trigger span and the
  // useEffect-driven setHoverEnabled(false) on touch devices forces a
  // structural rerender that detaches the original DOM — breaking any
  // ref/element captured before the second render (notably Testing
  // Library's `findByText` returning a stale, disconnected node).
  const [hoverEnabled] = useState<boolean>(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return true;
    }
    return window.matchMedia('(hover: hover)').matches;
  });
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const popoverId = useId();

  const shouldSuppressHover =
    !hoverEnabled || state.kind === 'loading' || state.kind === 'failed';

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const handlePointerLeave = useCallback(() => {
    clearTimers();
    hideTimerRef.current = setTimeout(() => {
      setOpen(false);
      hideTimerRef.current = null;
    }, HIDE_DELAY);
  }, [clearTimers]);

  const handlePointerEnter = useCallback(() => {
    if (shouldSuppressHover) {
      return;
    }
    clearTimers();
    showTimerRef.current = setTimeout(() => {
      setOpen(true);
      showTimerRef.current = null;
    }, SHOW_DELAY);
  }, [shouldSuppressHover, clearTimers]);

  const handlePopoverPointerEnter = useCallback(() => {
    if (shouldSuppressHover) {
      return;
    }
    clearTimers();
  }, [shouldSuppressHover, clearTimers]);

  const handlePopoverPointerLeave = useCallback(() => {
    handlePointerLeave();
  }, [handlePointerLeave]);

  const handleFocus = useCallback(() => {
    if (shouldSuppressHover) {
      return;
    }
    clearTimers();
    showTimerRef.current = setTimeout(() => {
      setOpen(true);
      showTimerRef.current = null;
    }, SHOW_DELAY);
  }, [shouldSuppressHover, clearTimers]);

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLSpanElement>) => {
      const related = e.relatedTarget as Node | null;
      if (triggerRef.current?.contains(related)) {
        return;
      }
      const popoverEl = document.getElementById(popoverId);
      if (popoverEl?.contains(related)) {
        return;
      }
      clearTimers();
      setOpen(false);
    },
    [popoverId, clearTimers]
  );

  const handlePopoverBlur = useCallback(
    (e: FocusEvent) => {
      const related = e.relatedTarget as Node | null;
      if (triggerRef.current?.contains(related)) {
        return;
      }
      const popoverEl = document.getElementById(popoverId);
      if (popoverEl?.contains(related)) {
        return;
      }
      clearTimers();
      setOpen(false);
    },
    [popoverId, clearTimers]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Escape' && open) {
        e.stopPropagation();
        clearTimers();
        setOpen(false);
        triggerRef.current
          ?.querySelector<HTMLElement>('[role="button"]')
          ?.focus();
      }
    },
    [open, clearTimers]
  );

  const handlePopoverKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        clearTimers();
        setOpen(false);
        triggerRef.current
          ?.querySelector<HTMLElement>('[role="button"]')
          ?.focus();
      }
    },
    [clearTimers]
  );

  useEffect(() => () => clearTimers(), [clearTimers]);

  // Cancel any pending show/hide and close the popover whenever
  // suppression activates (e.g. state transitions to loading/failed
  // or the hover-capability check resolves to false). Without this,
  // an in-flight SHOW_DELAY timer would still fire `setOpen(true)`
  // after suppression begins, leaving stale `open` state that would
  // surface instantly if suppression toggled back off.
  useEffect(() => {
    if (shouldSuppressHover) {
      clearTimers();
      setOpen(false);
    }
  }, [shouldSuppressHover, clearTimers]);

  const badgeProps: EntityBadgeProps = {
    state,
    variant,
    avatar,
    disabled,
    interactive,
    onClick,
    className,
    showEmail,
  };

  if (shouldSuppressHover) {
    return <EntityBadge {...badgeProps} />;
  }

  return (
    <span
      ref={triggerRef}
      // `min-w-0 max-w-full` lets the wrapper shrink inside a flex parent
      // so the underlying `EntityBadge` (which already has `min-w-0
      // max-w-full` + `truncate` on its display name) can ellipsis its
      // text when the row uses `flex-nowrap` (e.g. CRM list cells).
      //
      // The alignment comes from the badge's own map rather than a rule of our
      // own. This span is the inline-level box in the surrounding line box and
      // the badge is a flex item inside it, so `vertical-align` set on the
      // badge does nothing and whatever we put here is what the page gets. A
      // hardcoded `variant === 'inline'` lived here and silently outvoted the
      // badge when `record` was added — see `WRAPPER_ALIGN_CLASSES`.
      className={cx(
        'inline-flex min-w-0 max-w-full',
        WRAPPER_ALIGN_CLASSES[variant ?? 'default']
      )}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <EntityBadge
        {...badgeProps}
        ariaDescribedBy={open ? popoverId : undefined}
        ariaExpanded={open}
        ariaHasPopup='dialog'
      />
      <EntityHoverPopover
        open={open}
        triggerRef={triggerRef}
        placement={placement}
        id={popoverId}
        aria-label='Object preview'
        onPointerEnter={handlePopoverPointerEnter}
        onPointerLeave={handlePopoverPointerLeave}
        onBlur={handlePopoverBlur}
        onKeyDown={handlePopoverKeyDown}
      >
        {children}
      </EntityHoverPopover>
    </span>
  );
}
